function About() {
    const education = [
        {
            degree: 'Master of Science in Financial Data Science',
            school: 'University College Dublin Smurfit Graduate Business School',
            period: '2025 - 2026',
            description: 'Focused on quantitative finance, machine learning, and statistical modeling. Relevant coursework includes Time Series Analysis, Portfolio Theory, Risk Management, and Deep Learning.',
            logo: '/images/ucd_logo.jpeg'  // Note: starts with / and uses underscore
        },
        {
            degree: 'Bachelor of Science in Computer Science/Cybersecurity',
            school: 'Kean University',
            period: '2021 - 2025',
            description: 'Strong foundation in algorithms, data structures, high-performance computing, distributed systems',
            logo: '/images/kean_logo.jpeg'  // Note: starts with / and uses underscore
        }
    ];

    const experience = [
        {
            title: 'Research Assistant',
            company: 'University Research Lab',
            period: '2023 - 2024',
            responsibilities: [
                'Conducted research on time series forecasting for market volatility',
                'Implemented GARCH and ARIMA models for stock price prediction',
                'Published findings in university research journal'
            ]
        }
    ];

    return (
        <main style={{ maxWidth: '900px', margin: '0 auto', padding: '4rem 2rem' }}>
            {/* Introduction */}
            <section>
                <h1 style={{ marginBottom: '1.5rem' }}>About Me</h1>
                <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#555', marginBottom: '1rem' }}>
                    I'm a Financial Data Science graduate student with a passion for applying machine learning
                    and statistical methods to solve complex financial problems. My work focuses on the intersection
                    of finance, technology, and data analytics.
                </p>
                <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#555', marginBottom: '1rem' }}>
                    I specialize in building predictive models, analyzing market trends, and developing full-stack
                    applications that make financial data accessible and actionable. Whether it's forecasting stock
                    volatility, assessing credit risk, or creating interactive dashboards, I'm driven by the
                    challenge of turning data into insights.
                </p>
            </section>

            {/* Education */}
            <section style={{ marginTop: '4rem' }}>
                <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>Education</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    {education.map((edu, index) => (
                        <div
                            key={index}
                            style={{
                                padding: '1.5rem',
                                backgroundColor: '#FEFDFB',
                                borderRadius: '8px',
                                borderLeft: '4px solid #A0522D'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                                {/* University Logo */}
                                <img
                                    src={edu.logo}
                                    alt={`${edu.school} logo`}
                                    style={{
                                        width: '60px',
                                        height: '60px',
                                        objectFit: 'contain',
                                        flexShrink: 0,
                                        backgroundColor: '#FFF5EB',
                                        padding: '0.5rem',
                                        borderRadius: '8px'
                                    }}
                                />

                                {/* Education Details */}
                                <div style={{ flex: 1 }}>
                                    <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.3rem' }}>
                                        {edu.degree}
                                    </h3>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '0.75rem',
                                        flexWrap: 'wrap',
                                        gap: '0.5rem'
                                    }}>
                                        <p style={{ margin: 0, color: '#A0522D', fontWeight: '500', fontSize: '1.05rem' }}>
                                            {edu.school}
                                        </p>
                                        <p style={{ margin: 0, color: '#666', fontSize: '0.95rem' }}>
                                            {edu.period}
                                        </p>
                                    </div>
                                    <p style={{ margin: 0, color: '#666', lineHeight: '1.6' }}>
                                        {edu.description}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Experience */}
            <section style={{ marginTop: '4rem' }}>
                <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>Experience</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    {experience.map((exp, index) => (
                        <div
                            key={index}
                            style={{
                                padding: '1.5rem',
                                border: '1px solid #E8DDD0',
                                borderRadius: '8px',
                                backgroundColor: '#FEFDFB'
                            }}
                        >
                            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.3rem' }}>
                                {exp.title}
                            </h3>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '1rem',
                                flexWrap: 'wrap',
                                gap: '0.5rem'
                            }}>
                                <p style={{ margin: 0, color: '#A0522D', fontWeight: '500', fontSize: '1.05rem' }}>
                                    {exp.company}
                                </p>
                                <p style={{ margin: 0, color: '#666', fontSize: '0.95rem' }}>
                                    {exp.period}
                                </p>
                            </div>
                            <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#666', lineHeight: '1.8' }}>
                                {exp.responsibilities.map((resp, idx) => (
                                    <li key={idx} style={{ marginBottom: '0.5rem' }}>{resp}</li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </section>

            {/* What I'm Working On */}
            <section style={{ marginTop: '4rem' }}>
                <h2 style={{ fontSize: '2rem', marginBottom: '1.5rem' }}>What I'm Currently Working On</h2>
                <div style={{
                    padding: '2rem',
                    backgroundColor: '#FFF5EB',
                    borderRadius: '8px',
                    border: '1px solid #E8DDD0'
                }}>
                    <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#555', lineHeight: '1.8', fontSize: '1.05rem' }}>
                        <li style={{ marginBottom: '0.75rem' }}>
                            Building advanced ML models for financial market prediction
                        </li>
                        <li style={{ marginBottom: '0.75rem' }}>
                            Exploring deep learning applications in algorithmic trading
                        </li>
                        <li style={{ marginBottom: '0.75rem' }}>
                            Contributing to open-source financial analysis tools
                        </li>
                        <li style={{ marginBottom: '0.75rem' }}>
                            Completing my thesis on volatility forecasting using transformer models
                        </li>
                    </ul>
                </div>
            </section>
        </main>
    );
}

export default About;